// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TextField } from "@material-ui/core";
import { Box } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { SHORT_VIDEO_CREATE_MUTATION, SHORT_VIDEO_UPDATE_MUTATION } from "../cmsMutations";
import { SHORT_VIDEOS_QUERY } from "../cmsQueries";
import { CMSDeviceType, CMSPlatform, ShortVideo } from "../types";
import CmsToggle from "./CmsToggle";

interface ShortDialogProps {
  open: boolean;
  video?: ShortVideo | null;
  onClose: () => void;
}

const REDIRECT_OPTIONS = [
  { label: "None", value: "NONE" },
  { label: "Product", value: "PRODUCT" },
  { label: "Category", value: "CATEGORY" },
  { label: "Collection", value: "COLLECTION" },
  { label: "External URL", value: "URL" },
];

const PLATFORM_OPTIONS = [
  { label: "All Platforms (App & Web)", value: "ALL" },
  { label: "Mobile App Only", value: "APP" },
  { label: "Web App Only", value: "WEB" },
];

const DEVICE_OPTIONS = [
  { label: "All Devices", value: "ALL" },
  { label: "Mobile (Smartphones)", value: "MOBILE" },
  { label: "Tablet", value: "TABLET" },
  { label: "Desktop / Laptop", value: "DESKTOP" },
];

export const ShortDialog: React.FC<ShortDialogProps> = ({ open, video, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!video;

  const [title, setTitle] = useState(video?.title || "");
  const [subtitle, setSubtitle] = useState(video?.subtitle || "");
  const [description, setDescription] = useState(video?.description || "");
  const [durationSeconds, setDurationSeconds] = useState(video?.durationSeconds?.toString() || "");
  const [redirectType, setRedirectType] = useState(video?.redirectType || "NONE");
  const [redirectValue, setRedirectValue] = useState(video?.redirectValue || "");
  const [targetPlatform, setTargetPlatform] = useState<CMSPlatform>(video?.targetPlatform || "ALL");
  const [targetDevice, setTargetDevice] = useState<CMSDeviceType>(video?.targetDevice || "ALL");
  const [isPublished, setIsPublished] = useState(video ? video.isPublished : true);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  React.useEffect(() => {
    if (video) {
      setTitle(video.title || "");
      setSubtitle(video.subtitle || "");
      setDescription(video.description || "");
      setDurationSeconds(video.durationSeconds !== null && video.durationSeconds !== undefined ? video.durationSeconds.toString() : "");
      setRedirectType(video.redirectType || "NONE");
      setRedirectValue(video.redirectValue || "");
      setTargetPlatform(video.targetPlatform || "ALL");
      setTargetDevice(video.targetDevice || "ALL");
      setIsPublished(video.isPublished ?? true);
      setVideoFile(null);
      setThumbnailFile(null);
    } else {
      setTitle("");
      setSubtitle("");
      setDescription("");
      setDurationSeconds("");
      setRedirectType("NONE");
      setRedirectValue("");
      setTargetPlatform("ALL");
      setTargetDevice("ALL");
      setIsPublished(true);
      setVideoFile(null);
      setThumbnailFile(null);
    }
  }, [video, open]);

  const [createShort, { loading: creating }] = useMutation(SHORT_VIDEO_CREATE_MUTATION, {
    refetchQueries: [{ query: SHORT_VIDEOS_QUERY }],
    onCompleted: data => {
      if (data.shortVideoCreate?.errors?.length) {
        notify({ status: "error", text: data.shortVideoCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Short video created successfully" });
        onClose();
      }
    },
  });

  const [updateShort, { loading: updating }] = useMutation(SHORT_VIDEO_UPDATE_MUTATION, {
    refetchQueries: [{ query: SHORT_VIDEOS_QUERY }],
    onCompleted: data => {
      if (data.shortVideoUpdate?.errors?.length) {
        notify({ status: "error", text: data.shortVideoUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Short video updated successfully" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!title) {
      notify({ status: "error", text: "Title is required" });
      return;
    }
    if (!isEdit && !videoFile) {
      notify({ status: "error", text: "Video file is required" });
      return;
    }

    const input: any = {
      title,
      subtitle,
      description,
      redirectType,
      redirectValue,
      targetPlatform,
      targetDevice,
      isPublished,
    };
    if (durationSeconds) {
      input.durationSeconds = parseInt(durationSeconds, 10);
    }
    if (videoFile) {
      input.video = videoFile;
    }
    if (thumbnailFile) {
      input.thumbnail = thumbnailFile;
    }

    if (isEdit) {
      updateShort({ variables: { id: video.id, input } });
    } else {
      createShort({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit Short Video" : "Upload Short Video Reel"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
        />
        <TextField
          label="Subtitle"
          fullWidth
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <TextField
          label="Duration (Seconds) - Manual Input"
          fullWidth
          type="number"
          value={durationSeconds}
          onChange={e => setDurationSeconds(e.target.value)}
          helperText="e.g. 15, 30, 45 seconds"
        />

        <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={3}>
          <Select
            label="Target Platform"
            name="targetPlatform"
            value={targetPlatform}
            options={PLATFORM_OPTIONS}
            onChange={({ target }) => setTargetPlatform(target.value as any)}
          />
          <Select
            label="Target Device"
            name="targetDevice"
            value={targetDevice}
            options={DEVICE_OPTIONS}
            onChange={({ target }) => setTargetDevice(target.value as any)}
          />
        </Box>

        <Select
          label="Redirect Type"
          name="redirectType"
          value={redirectType}
          options={REDIRECT_OPTIONS}
          onChange={({ target }) => setRedirectType(target.value as any)}
        />
        {redirectType !== "NONE" && (
          <TextField
            label="Redirect Value (Product ID / Category ID / URL)"
            fullWidth
            value={redirectValue}
            onChange={e => setRedirectValue(e.target.value)}
          />
        )}
        <Box display="flex" flexDirection="column" gap={1}>
          <label style={{ fontSize: "0.85rem", color: "#666" }}>
            Video File (.mp4) {video?.videoUrl && "(Upload new to replace)"}
          </label>
          <input
            type="file"
            accept="video/*"
            onChange={e => setVideoFile(e.target.files?.[0] || null)}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <label style={{ fontSize: "0.85rem", color: "#666" }}>
            Thumbnail Poster Image (Optional) {video?.thumbnailUrl && "(Upload new to replace)"}
          </label>
          {video?.thumbnailUrl && (
            <Box marginBottom={1}>
              <img
                src={video.thumbnailUrl}
                alt={video.title}
                style={{ maxHeight: 90, maxWidth: 65, objectFit: "cover", borderRadius: 4, border: "1px solid #e2e8f0" }}
              />
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={e => setThumbnailFile(e.target.files?.[0] || null)}
          />
        </Box>
        <CmsToggle
          label="Published & Active on Storefront"
          description="Make this short video reel visible immediately in customer feeds"
          checked={isPublished}
          onChange={setIsPublished}
        />
      </Box>
    </ActionDialog>
  );
};

export default ShortDialog;
